import type { Appointment, AppointmentSlot, Day, TimeRange } from '../models/repair-bookings';

export const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
};

export const formatDayLabel = (rawDate: string): string => {
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    const date = new Date(rawDate + 'T00:00:00');
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${dayNames[date.getDay()]})`;
};

const splitDateTime = (datetime: string) => {
    const [datePart, timePart] = datetime.split(' ');
    return {
        datePart: datePart ?? datetime,
        timePart: timePart ?? '',
    };
};

export const extractHourMinute = (datetime: string): string => {
    const { timePart } = splitDateTime(datetime);
    return timePart ? timePart.replace(/:\d+$/, '') : datetime;
};

export const getTimeSlotString = (slot: AppointmentSlot): string => {
    const { datePart } = splitDateTime(slot.start_at);
    return `${formatDayLabel(datePart)} ${extractHourMinute(slot.start_at)}〜`;
};

export const buildDayOptions = (appointments: Appointment[]): Day[][] => {
    const days = appointments.map((a) => {
        const label = formatDayLabel(a.date);
        const splitIndex = label.indexOf('(');
        return {
            rawDate: a.date,
            date: label.slice(0, splitIndex),
            day: label.slice(splitIndex),
        };
    });

    return Array.from({ length: Math.ceil(days.length / 7) }, (_, i) =>
        days.slice(i * 7, i * 7 + 7)
    );
};

export const buildTimeRanges = (appointments: Appointment[]): TimeRange[] => {
    const times = appointments.flatMap((a) =>
        a.slots.map((s) => ({
            start: extractHourMinute(s.start_at),
        }))
    );

    const minTime = times.reduce((min, t) => (t.start < min ? t.start : min), '24:00');
    const maxTime = times.reduce((max, t) => (t.start > max ? t.start : max), '00:00');

    if (!times.length || minTime >= maxTime) {
        return [];
    }

    const minHour = Math.floor(parseInt(minTime, 10) / 2) * 2;
    const ranges: TimeRange[] = [];
    for (let h = minHour; ; h += 2) {
        const start = `${String(h).padStart(2, '0')}:00`;
        if (start >= maxTime) {
            break;
        }
        const end = `${String(h + 2).padStart(2, '0')}:00`;
        ranges.push({ start, end: end > maxTime ? maxTime : end });
    }
    return ranges;
};

export const generateSlotsInRange = (
    date: string,
    time: TimeRange,
    apiSlots: AppointmentSlot[]
): AppointmentSlot[] => {
    const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h !== undefined && m !== undefined ? h * 60 + m : 0;
    };
    const format = (m: number) =>
        `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

    const startMin = toMinutes(time.start);
    const endMin = toMinutes(time.end);
    const slots: AppointmentSlot[] = [];

    for (let m = startMin; m < endMin; m += 30) {
        const slotStart = `${date} ${format(m)}:00`;
        const apiSlot = apiSlots.find((s) => s.start_at === slotStart);
        slots.push(apiSlot ?? { appointment_id: '', start_at: slotStart });
    }
    return slots;
};
